import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom, forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TreetopPropertiesProvider } from 'shared/util-common';

import { MaintenanceDto, FeatureFlagDto } from '@treetop/shared/model';

export type FeatureFlags = {
  editorEnabled: boolean;
};

@Injectable({ providedIn: 'root' })
export class FeatureFlagService {
  private http = inject(HttpClient);

  private baseUrl = `${TreetopPropertiesProvider.properties.baseUrl}api`;

  private readonly _featureFlags = signal<FeatureFlags>({
    editorEnabled: false,
  });

  private readonly _maintenance = signal<MaintenanceDto>(undefined);
  public maintenance = this._maintenance.asReadonly();

  public editorEnabled = computed(() => this._featureFlags().editorEnabled);

  public async init(): Promise<{ maintenance: MaintenanceDto; featureFlags: FeatureFlagDto }> {
    const result = await firstValueFrom(
      forkJoin({
        maintenance: this.getMaintenance(),
        featureFlags: this.loadFeatureFlags(),
      }),
    );

    this._maintenance.set(result.maintenance);

    this._featureFlags.set({
      editorEnabled: result.featureFlags.isEditorEnabled,
    });

    return result;
  }

  public getMaintenance(): Observable<MaintenanceDto> {
    return this.http.get<MaintenanceDto>(`${this.baseUrl}/version/is-maintenance-scheduled`);
  }

  public loadFeatureFlags(): Observable<FeatureFlagDto> {
    return this.http.get<string[]>(`${this.baseUrl}/features/feature-flags`).pipe(map((list) => this.mapFeatureFlags(list)));
  }

  private mapFeatureFlags(featureList: string[]): FeatureFlagDto {
    return {
      isFlowchartEnabled: featureList.includes('IsNewFlowchartEnabled'),
      isEditorEnabled: featureList.includes('IsEditorEnabled'),
    };
  }
}
